import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/layout/header.component';
import Asaid from '../../../components/layout/asaid.component';
import Footer from '../../../components/layout/footer.component';
import PrincipalCard from '../../../components/cards/principal-card.component';
import Datatable from '../../../components/datatable/datatable.component';
import ActionsDropdown from '../../../components/datatable/actions-dropdown.component';
import Icon from '../../../components/forms/icon.component';
import useBgPages from '../../../hooks/use-bg-pages.hook';
import useRequest from '../../../hooks/use-request.hook';
import LogData from '../../../components/logs/log-data.component';
import { AlertService } from '../../../services/alert.service';
import { useSession } from '../../../hooks/use-session.hook';
import { PROFILES } from '../../../constants';
import DateRegister from '../../../components/datatable/register-data.component';
import { TemplatesService } from '../../../services/templates.service';
import PreviewModal from './modal/preview.modal';

interface MailingTemplate {
  _id: string;
  account: {
    name: string;
    company: {
      name: string;
    };
  };
  name: string;
  html: string;
  status: boolean;
  created_at: string;
  updated_at: string;
}

function TemplatesMailingPage() {
  useBgPages();
  const { t } = useTranslation();
  const { session } = useSession();
  const { call, isRequesting } = useRequest();
  const navigate = useNavigate();

  const [rows, setRows] = useState<MailingTemplate[]>([]);
  const [showPreviewModal, setShowPreviewModal] = useState<{
    show: boolean;
    data: MailingTemplate | null;
  }>({ show: false, data: null });
  const [totalTemplates, setTotalTemplates] = useState(0);
  const [activeTemplates, setActiveTemplates] = useState(0);

  const dataStatistics = [
    {
      label: t('templates.mailing.statistics.total'),
      total: totalTemplates,
      type: '',
      icon: 'ki-message-text-2'
    },
    {
      label: t('templates.mailing.statistics.active'),
      total: activeTemplates,
      type: 'primary',
      icon: 'ki-check-circle'
    }
  ];

  const handleShowPreviewModal = () => {
    setShowPreviewModal({ show: false, data: null });
  };

  const StatusBadge = ({ status }: { status: boolean }) => {
    if (status) {
      return (
        <span className="badge text-dark fs-6">
          <Icon icon="ki-check-circle" size="fs-3" type="primary" />
          {t('templates.mailing.status.active')}
        </span>
      );
    }

    return (
      <span className="badge text-danger fs-6">
        <Icon icon="ki-minus-circle" size="fs-3" type="danger" />
        {t('templates.mailing.status.inactive')}
      </span>
    );
  };

  const columns = [
    {
      name: t('templates.mailing.table.template-info'),
      sortable: true,
      selector: (r: any) => `${r.name} ${r.account?.company?.name} ${r.account?.name}`,
      width: 5,
      cell: (template: MailingTemplate) => {
        return (
          <ul className="list-group-item text-nowrap">
            <li className="list-group-item">
              <div className="d-flex flex-column">
                <div className="w-100 mb-1">
                  <strong className="fs-6">{t('templates.mailing.table.name')}:</strong>
                  <span className="ms-1">{template?.name || t('general.no-info')}</span>
                </div>
                {session.profile === PROFILES.ROOT && (
                  <>
                    <div className="w-100 mb-1">
                      <strong className="fs-6">{t('templates.mailing.table.company')}:</strong>
                      <span className="ms-1">
                        {template.account?.company?.name || t('general.no-info')}
                      </span>
                    </div>
                    <div className="w-100 mb-1">
                      <strong className="fs-6">{t('templates.mailing.table.account')}:</strong>
                      <span className="ms-1">
                        {template?.account?.name || t('general.no-info')}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </li>
          </ul>
        );
      }
    },
    {
      name: t('templates.mailing.table.status'),
      sortable: true,
      selector: (r: any) => `${r.status}`,
      width: 10,
      cell: (template: MailingTemplate) => <StatusBadge status={template.status} />
    },
    {
      name: t('templates.mailing.table.preview'),
      sortable: true,
      selector: (r: any) => `${r._id}`,
      width: 10,
      cell: (template: MailingTemplate) => (
        <button
          className="btn btn-light btn-sm hover-scale fw-bold"
          onClick={() => setShowPreviewModal({ show: true, data: template })}
          title="Vista previa"
        >
          <Icon icon="ki-eye" size="fs-2" />
          <span>{t('general.preview')}</span>
        </button>
      )
    },
    {
      name: t('templates.mailing.table.date'),
      sortable: true,
      selector: (r: any) => `${r.created_at}`,
      width: 10,
      cell: (template: MailingTemplate) => <DateRegister created_at={template.created_at} />
    },
    {
      name: t('templates.mailing.table.actions'),
      sortable: true,
      selector: (r: any) => `${r._id}`,
      width: 10,
      cell: (template: MailingTemplate) => (
        <ActionsDropdown
          rowStatus={template.status}
          edit={() =>
            navigate(`/templates-panel/templates-mailing-builder`, {
              state: { template: template }
            })
          }
          delete={() => handleDeleteTemplate(template._id, template.name)}
          changeStatus={() =>
            handleChangeStatus(
              template._id,
              'changeStatusTemplatesMailing-4548604568',
              template.status
            )
          }
        />
      )
    }
  ];

  const handleDeleteTemplate = async (id: string, name: string) => {
    try {
      const confirmed = await AlertService.question(
        t('general.atention'),
        t('templates.mailing.confirmations.delete-message', { name }),
        null as any,
        t('templates.mailing.confirmations.delete-button')
      );
      if (!confirmed) {
        return;
      }

      await TemplatesService.deleteTemplateMailing(id);

      AlertService.toastSuccess(t('templates.mailing.messages.delete-success'));
      await getTemplates();
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const handleChangeStatus = async (id: string, buttonId: string, status: boolean) => {
    const isConfirmed = await AlertService.warning(
      t('general.atention'),
      t('templates.mailing.q1'),
      () => Promise.resolve(true)
    );
    if (!isConfirmed) return;
    try {
      await TemplatesService.changeStatusTemplatesMailing({
        _id: id,
        status: status ? false : true
      });
      AlertService.toastSuccess(t('templates.mailing.success'));
      await getTemplates();
      LogData({ id: id, status: status ? 'disable' : 'enable' }, buttonId, window.location.href);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const getTemplates = async () => {
    try {
      const templatesData = await call(() => TemplatesService.getAllTemplatesMailing());

      setRows(templatesData);
      setTotalTemplates(templatesData.length);
      setActiveTemplates(templatesData.filter((t: any) => t.status).length);
    } catch (err: any) {
      console.log('Error: ', err);
      AlertService.toastError(err?.error || 'Error al cargar plantillas');
    }
  };

  useEffect(() => {
    getTemplates();
  }, []);

  return (
    <>
      <div className="page d-flex flex-row flex-column-fluid">
        <Asaid />
        <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
          <Header />
          <div className="content fs-6 d-flex flex-column flex-column-fluid" id="kt_content">
            <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
              <div className="container-xxl">
                <PrincipalCard
                  dataStatistics={dataStatistics}
                  btnRefresh
                  onRefresh={async () => {
                    await getTemplates();
                    AlertService.toastSuccess('¡Éxito!, información actualizada.');
                  }}
                  btnBack
                  title={t('templates.mailing.title')}
                  titleBolder={t('templates.mailing.title-bold')}
                  subtitle={t('templates.mailing.subtitle')}
                  breadcrumbItems={[t('asaid.link-templates')]}
                  textButton={t('templates.mailing.create-button')}
                  iconButton="ki-soft-2"
                  btnCreate
                  onClick={() => navigate('/templates-panel/templates-mailing-builder')}
                />

                <div className="card mb-5 mb-xl-10">
                  <div className="card-header align-items-center border-0 mt-5">
                    <div className="card-title align-items-start flex-column animate-fadeInUp animate-delay-1 h3">
                      <span className="fw-bolder fw-bold fs-2">{t('templates.mailing.title')}</span>
                      <span className="text-gray-700 mt-2 fw-semibold fs-6">
                        {t('templates.mailing.table.registered-available')}
                      </span>
                    </div>
                    <div className="card-toolbar"></div>
                  </div>
                  <div className="card-body pt-1">
                    <Datatable columns={columns} data={rows} isLoading={isRequesting} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>

      <PreviewModal
        show={showPreviewModal.show}
        onRequestClose={handleShowPreviewModal}
        template={showPreviewModal.data}
      />
    </>
  );
}

export default TemplatesMailingPage;
